import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Language = 'en' | 'fr';

const STORAGE_KEY = 'head-of-trading.language';

interface LanguageContextValue {
  readonly language: Language;
  readonly locale: 'en-US' | 'fr-FR';
  readonly setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

interface LanguageStorage {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => void;
}

export function resolveLanguagePreference(storage?: LanguageStorage): Language {
  try {
    return storage?.getItem(STORAGE_KEY) === 'fr' ? 'fr' : 'en';
  } catch {
    return 'en';
  }
}

export function persistLanguagePreference(storage: LanguageStorage | undefined, language: Language): void {
  try {
    storage?.setItem(STORAGE_KEY, language);
  } catch {
    // Storage can be unavailable in hardened/private browser contexts.
  }
}

export function applyDocumentLanguage(target: { readonly documentElement: { lang: string } }, language: Language): void {
  target.documentElement.lang = language;
}

function storedLanguage(): Language {
  return resolveLanguagePreference(window.localStorage);
}

export function LanguageProvider({ children }: { readonly children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(storedLanguage);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    persistLanguagePreference(window.localStorage, next);
  }, []);

  useEffect(() => {
    applyDocumentLanguage(document, language);
  }, [language]);

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    locale: language === 'fr' ? 'fr-FR' : 'en-US',
    setLanguage
  }), [language, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const value = useContext(LanguageContext);
  if (!value) throw new Error('useLanguage must be used inside LanguageProvider.');
  return value;
}

export function LanguageSwitcher({ className = '' }: { readonly className?: string }) {
  const { language, setLanguage } = useLanguage();
  const label = language === 'fr' ? 'Choix de la langue' : 'Language selection';

  return (
    <div className={`language-switcher ${className}`.trim()} role="group" aria-label={label}>
      <button type="button" className={language === 'en' ? 'active' : ''} aria-pressed={language === 'en'} onClick={() => setLanguage('en')}>EN</button>
      <button type="button" className={language === 'fr' ? 'active' : ''} aria-pressed={language === 'fr'} onClick={() => setLanguage('fr')}>FR</button>
    </div>
  );
}
